import {
  CMVarAggregationLiteType,
  CMVarAggregationType,
  CMVArrayToMap,
  CMVMapToArray,
  DayHour,
  DayNumber,
  DeepPartial,
  MapLike,
  Milliseconds,
  MonthNumber,
  QuarterIdx,
  ValidBusinessObject,
} from '@iotv/iotv-v3-types'
import { enumerable } from '../../enumerableDecorator'

import { fillWithXByArrayDef } from '../../../../util/objects/objectOperations'
import { ModelCommon } from '../../../ModelCommon'
import { CMVarAggregationObject } from '../../CMVarAggregation'
import { HourlyYearBasedArrayDef } from '../sharedTypes'
import {
  DayOfHourlyStructure,
  HourlyStructure,
  HourlyYearBasedAccumulatorAccessor,
  HourlyYearBasedAccumulatorCMVKeyAccessor,
  MonthOfHourlyStructure,
} from './types'

const className = 'NormalAccumulator'

/**
 * Aggregates the results of Coverage Map Variables into a single {@link CMVarAggregationObject}
 *
 * @param cmvs Coverage Map Variable Aggregations
 * @param accumulateMinMax Whether to take the min or max of the supplied {@link CMVarAggregation}, or to accumulate them all together
 * @returns
 */
export const aggregateCMVs = (cmvs: CMVarAggregationType[], accumulateMinMax?: boolean): CMVarAggregationType => {
  const aggrCMV = new CMVarAggregationObject()
  cmvs.forEach((cmv) => {
    aggrCMV.mean = (aggrCMV.mean * aggrCMV.cn + cmv.mean * cmv.cn) / (aggrCMV.cn + cmv.cn)
    aggrCMV.cn += cmv.cn
    aggrCMV.firstTimestamp = aggrCMV.firstTimestamp
      ? cmv.firstTimestamp
        ? Math.min(aggrCMV.firstTimestamp, cmv.firstTimestamp)
        : aggrCMV.firstTimestamp
      : cmv.firstTimestamp
    aggrCMV.lastTimeStamp = aggrCMV.lastTimeStamp
      ? cmv.lastTimeStamp
        ? Math.max(aggrCMV.lastTimeStamp, cmv.lastTimeStamp)
        : aggrCMV.lastTimeStamp
      : cmv.lastTimeStamp
    if (accumulateMinMax) {
      aggrCMV.max = (aggrCMV.max ?? 0) + (cmv.max ?? 0)
      aggrCMV.min = (aggrCMV.min ?? 0) + (cmv.min ?? 0)
    } else {
      aggrCMV.max = Math.max(aggrCMV.max ?? cmv.max ?? -Infinity, cmv.max ?? aggrCMV.max ?? -Infinity)
      aggrCMV.min = Math.min(aggrCMV.min ?? cmv.min ?? Infinity, cmv.min ?? aggrCMV.min ?? Infinity)
    }
    aggrCMV.sqDiffFromMean += cmv.sqDiffFromMean // This is not accurate!!!
  })

  if (aggrCMV.min === Infinity) aggrCMV.min = undefined
  if (aggrCMV.max === -Infinity) aggrCMV.max = undefined

  return aggrCMV
}

/** Get accessor from timestamp
 * Doesn't deal with local time, but if you always access through the accessor you'll be fine.
 * This does mean there's some weirdness with your local timezone if you view the accumulator directly
 * E.g. in NZ, the normal for 4 Dec 14:00 is actually in [11][3][1] (month 11, day 3, hour 1)
 */
export const hourlyYearBasedAccessorFromTimestamp = (timestamp: number): HourlyYearBasedAccumulatorAccessor => {
  const date = new Date(timestamp)
  const idx = {
    month: date.getUTCMonth() as MonthNumber,
    day: (date.getUTCDate() - 1) as DayNumber,
    hour: date.getUTCHours() as DayHour,
  }

  return idx
}

export class NormalAccumulator<DimensionedArr> extends ModelCommon {
  constructor(ob?: ValidBusinessObject) {
    super(ob?.type ?? className, (({ sk, pk, type, id, name }: ValidBusinessObject) => ({ sk, pk, type, id, name }))(ob ?? ({} as ValidBusinessObject)))
    if (ob) {
      if (ob.accumulator instanceof Array) {
        this.ingest(ob)
      } else {
        this.accumulator = ob.accumulator
        this.keyMap = ob.keyMap
      }
    }
  }
  accumulator: DimensionedArr | undefined
}

export type DehydratedHourlyYearBasedNormalStructure<T> = T[][][]
export type DehydratedHourlyYearBasedCMV = Partial<ValidBusinessObject> & {
  accumulator: DehydratedHourlyYearBasedNormalStructure<MapLike<CMVarAggregationLiteType>>
  keyMap?: MapLike<string>
}
export type ValidCMVFields = MapLike<CMVarAggregationType> | undefined

export class HourlyYearBasedNormalAccumulator extends NormalAccumulator<MonthOfHourlyStructure<ValidCMVFields>> {
  constructor(ob?: ValidBusinessObject & ({ accumulator: MonthOfHourlyStructure<ValidCMVFields> } | DehydratedHourlyYearBasedCMV)) {
    super(ob)
    if (!ob) {
      this.type = 'HourlyYearBasedNormalAccumulator'
      this.initialize()
    }
  }

  keyMap?: MapLike<string>

  @enumerable(false)
  initialize = () => {
    const xFn = () => ({})
    const dehydrated: DehydratedHourlyYearBasedCMV = { accumulator: fillWithXByArrayDef(HourlyYearBasedArrayDef, xFn)() }
    this.ingest(dehydrated)
  }

  @enumerable(false)
  getDimensions = (): number[] => {
    let dimStr: number[] = []
    let map: MapLike<any> | undefined = this.accumulator
    do {
      map && dimStr.push(Object.keys(map).length)
      map = map?.[0]
    } while (map)

    return dimStr
  }

  @enumerable(false)
  xFn = () => undefined

  @enumerable(false)
  ingest = ({ accumulator: HourlyYearBasedAccumulation, keyMap, sk, pk, type, id, name }: DehydratedHourlyYearBasedCMV) => {
    this.keyMap = keyMap // this must be intiialized first otherwise will get bad keys
    const months: DeepPartial<MonthOfHourlyStructure<ValidCMVFields>> = {}
    Object.entries(HourlyYearBasedAccumulation).forEach(([i, dayArray]) => {
      const days: DeepPartial<DayOfHourlyStructure<ValidCMVFields>> = {}
      Object.entries(dayArray).forEach(([i, hourArray]) => {
        const hours: DeepPartial<HourlyStructure<ValidCMVFields>> = {}
        Object.entries(hourArray).forEach(([i, vmvLite]) => {
          const cmv: ValidCMVFields = {}
          Object.entries(vmvLite).forEach(([shortCMVKey, v]) => (cmv[this.getOriginalCMVKey(shortCMVKey)] = CMVArrayToMap(vmvLite[shortCMVKey])))
          hours[parseInt(i) as QuarterIdx] = cmv
        })
        days[parseInt(i) as DayNumber] = hours
      })
      months[parseInt(i) as MonthNumber] = days
    })
    this.accumulator = months as MonthOfHourlyStructure<ValidCMVFields>

    sk && (this.sk = sk)
    pk && (this.pk = pk)
    type && (this.type = type)
    id && (this.id = id)
    name && (this.name = name)
  }

  @enumerable(false)
  egest = (): DehydratedHourlyYearBasedCMV | undefined =>
    this.accumulator
      ? { ...this.dehydrate(this.accumulator), keyMap: this.keyMap, type: this.type, pk: this.pk, sk: this.sk, id: this.id, name: this.name }
      : undefined

  @enumerable(false)
  getShortCMVKey = (k: string): string => {
    let shortCMVKey = k
    if (!this.keyMap) {
      this.keyMap = {}
    }
    if (this.keyMap?.[k]) {
      shortCMVKey = this.keyMap[k]
    } else {
      const keyMapEntriesNum = Object.keys(this.keyMap).length
      shortCMVKey = String.fromCharCode(keyMapEntriesNum + 65)
      this.keyMap[k] = shortCMVKey
    }
    return shortCMVKey
  }

  @enumerable(false)
  getOriginalCMVKey = (shortKey: string): string => {
    let originalCMVKey = shortKey
    if (this.keyMap) {
      const matchedEntry = Object.entries(this.keyMap).find(([k, v]) => v === shortKey)
      if (matchedEntry) {
        const [ok, v] = matchedEntry
        originalCMVKey = ok
      }
    }

    return originalCMVKey
  }

  @enumerable(false)
  dehydrate = (acc: MonthOfHourlyStructure<ValidCMVFields>): DehydratedHourlyYearBasedCMV => {
    const dehydrated: DehydratedHourlyYearBasedCMV['accumulator'] = []
    Object.values(acc).forEach((months, m) => {
      dehydrated[m] = []
      Object.values(months).forEach((days, d) => {
        dehydrated[m][d] = []
        Object.values(days).forEach((hours, h) => {
          if (hours) {
            dehydrated[m][d][h] = Object.fromEntries(Object.entries(hours).map(([k, cmv]) => [this.getShortCMVKey(k), CMVMapToArray(cmv)]))
          } else {
            dehydrated[m][d][h] = {}
          }
        })
      })
    })
    return { accumulator: dehydrated }
  }

  getByTimestamp = (timestamp: number, cmvKey: string) => {
    const idx = this.idxFromTimestamp(timestamp, cmvKey)

    return this.getByIndex(idx)
  }

  getByIndex = ({ month, day, hour, cmvKey }: HourlyYearBasedAccumulatorCMVKeyAccessor) => {
    //console.log("Getting by index", month, day, hour);
    const cmv = this.accumulator?.[month][day][hour]?.[cmvKey]
    return cmv
  }

  setByIndex = ({ month, day, hour, cmvKey }: HourlyYearBasedAccumulatorCMVKeyAccessor, cmv: CMVarAggregationObject) => {
    if (this.accumulator?.[month]?.[day]?.[hour]) {
      const sector = this.accumulator[month][day][hour]
      if (sector) {
        sector[cmvKey] = cmv
      }
    } else {
      const msg = `Can't find accessor ${Object.values({ month, day, hour, cmvKey })}`
      //console.log(`${msg} on this with accumulator`, this.accumulator)
      throw new Error(msg)
    }
  }

  // In datalifecycle, the timestamp isn't passed, so even if we update some time in the past, it will assume the update is happening now. Not really a huge deal though
  updateByIndex = (accessor: HourlyYearBasedAccumulatorCMVKeyAccessor, value: number | null, timestamp?: number) => {
    if (value !== null && value !== undefined) {
      const cmvZombie = this.getByIndex(accessor)
      const cmv = new CMVarAggregationObject(cmvZombie)
      cmv.update(value, timestamp)
      this.setByIndex(accessor, cmv)
    }
  }

  idxFromTimestamp = (timestamp: number, cmvKey: string) => {
    const idx = {
      ...hourlyYearBasedAccessorFromTimestamp(timestamp),
      cmvKey,
    }

    return idx
  }

  /** Gets the normal for a given period of time, assembled from hourly normals accumulated.
   * @param timestampRange - of the form [fromMs, toMs]
   * @param cmvKey - The key in the accumulator to get the normal for
   * @returns a CMV with the desired normal. Not that the CMV is not accumulated itself, such that you'll get the normal for the entire period. That is to say, if you give 2 hours, you'll get what is normal for two hours, not the hourly average within those two hours
   * */
  // TODO: add a way of getting prior period equal in length to the one we're getting and accumulating it
  getNormalForPeriod = (timestampRange: [number, number], cmvKey: string) => {
    let debug = false

    let cmvs = []

    if (timestampRange[1] < timestampRange[0]) return undefined

    // Need to find the overhang

    //console.log(`Getting normal for ${new Date(timestampRange[0])} ${new Date(timestampRange[1])}`)
    const firstPortion = (Milliseconds.HOUR - (timestampRange[0] % Milliseconds.HOUR)) / Milliseconds.HOUR
    //console.log("First Portion", firstPortion);

    let lastPortion = (timestampRange[1] % Milliseconds.HOUR) / Milliseconds.HOUR
    if (lastPortion < 0.001) lastPortion = 0

    //console.log("Last Portion", lastPortion);

    // Scale just one CMV if range is contained within 1 hour
    if (timestampRange[1] - timestampRange[0] < Milliseconds.HOUR /* Need to check they're within the same hour*/) {
      // Within one hour -> return one scaled CMV
      const firstIdx = hourlyYearBasedAccessorFromTimestamp(timestampRange[0])
      const lastIdx = hourlyYearBasedAccessorFromTimestamp(timestampRange[1])
      if (JSON.stringify(firstIdx) == JSON.stringify(lastIdx)) {
        const portion = (timestampRange[1] - timestampRange[0]) / Milliseconds.HOUR
        const cmv = this.getByTimestamp(timestampRange[0], cmvKey)
        if (!cmv) return undefined
        const singleScaledCMV = {
          mean: cmv.mean * portion, // I don't know if this is sound
          cn: cmv.cn * portion,
          sqDiffFromMean: cmv.sqDiffFromMean * portion,
          firstTimestamp: timestampRange[0],
          lastTimeStamp: timestampRange[1],
          min: cmv.min,
          max: cmv.max,
        }
        return new CMVarAggregationObject(singleScaledCMV)
      }
    }

    for (let t = timestampRange[0]; t < timestampRange[1] + Milliseconds.HOUR; t += Milliseconds.HOUR) {
      // Extended by an hour to catch the last CMV if we only need a portion
      const idx = this.idxFromTimestamp(t, cmvKey)
      const cmv = this.getByIndex(idx)
      if (!cmv) continue

      if (t == timestampRange[0]) {
        // Scale CMV by portion
        const scaledCmv: CMVarAggregationType = {
          mean: cmv.mean * firstPortion, // I don't know if this is sound
          cn: cmv.cn * firstPortion,
          sqDiffFromMean: cmv.sqDiffFromMean * firstPortion,
          firstTimestamp: timestampRange[0],
          lastTimeStamp: cmv.lastTimeStamp,
          min: cmv.min,
          max: cmv.max,
        }
        cmvs.push(scaledCmv)
      } else if (t > timestampRange[1] - Milliseconds.HOUR) {
        if (lastPortion > 0) {
          //console.log(lastPortion)
          const scaledCmv: CMVarAggregationType = {
            mean: cmv.mean * lastPortion, // I don't know if this is sound
            cn: cmv.cn * lastPortion,
            sqDiffFromMean: cmv.sqDiffFromMean * lastPortion,
            firstTimestamp: cmv.firstTimestamp,
            lastTimeStamp: timestampRange[1],
            min: cmv.min,
            max: cmv.max,
          }
          cmvs.push(scaledCmv)
        }
      } else {
        cmvs.push(cmv)
      }

      //let date = new Date(`${new Date(Date.now()).getFullYear()}-${idx.month}-${idx.day} ${idx.hour}:00`)
      //if (cmv) normalBucketed.push({ timestamp: date.valueOf(), state: cmv }, true, date.valueOf() - Milliseconds.HOUR)
    }

    if (cmvs.length == 0) return null

    //console.log(cmvs);
    const aggrNormal = aggregateCMVs(cmvs)
    //console.log(aggrNormal);

    // Given that we want the normal for a period longer than 1 hour, I think adding the mean is actually accurate ?
    // E.g. we have the normal for  hour 1 and hour 2, but we want the mean for a 2 hour period. Aggregating would give the mean for a 1 hour period
    debug && console.log('CMVs found', cmvs)
    const addedNormal = cmvs.reduce(
      (total, cmv) => {
        return {
          mean: total.mean + cmv.mean,
          cn: total.cn + cmv.cn,
          sqDiffFromMean: total.sqDiffFromMean ?? 0 + cmv.sqDiffFromMean ?? 0, // This is completely wrong, idk if we can find this
          firstTimestamp: (cmv.firstTimestamp ?? Infinity) < (total.firstTimestamp ?? Infinity) ? cmv.firstTimestamp : total.firstTimestamp,
          lastTimeStamp: (cmv.lastTimeStamp ?? -Infinity) > (total.lastTimeStamp ?? -Infinity) ? cmv.lastTimeStamp : total.lastTimeStamp,
          min: (cmv.min ?? Infinity) < (total.min ?? Infinity) ? cmv.min : total.min,
          max: (cmv.max ?? -Infinity) > (total.max ?? -Infinity) ? cmv.max : total.max,
        }
      },
      { mean: 0, cn: 0, sqDiffFromMean: 0, firstTimestamp: undefined, lastTimeStamp: undefined, min: Infinity, max: -Infinity },
    )

    const addedCMV = new CMVarAggregationObject(addedNormal)
    //console.log(addedCMV);
    return addedCMV
  }
}
